import { CouponBulkActionType, couponApis } from "@payping/api";
import { Archive, Bolt, BoltOff, Delete, UnArchiveOutlined } from "@payping/icons";
import { Checkbox, Modal, Space, Toast, Typography } from "@payping/web-components";
import React from "react";
import { useAsyncFn } from "react-use";

import { useAppTranslation } from "@/utils/useAppTranslation";
import type { ListCardProps } from "@payping/web-components/legacyComponents/List/types";

interface Props {
  retry: () => void;
  isArchived?: boolean;
  selectedRowKeys: string[];
  setSelectedRowKeys: React.Dispatch<React.SetStateAction<string[]>>;
  data: ListCardProps["dataSource"];
}

export const ListBulkActions: React.FC<Props> = (props) => {
  const { setSelectedRowKeys, selectedRowKeys, data = [], isArchived, retry } = props;
  const { t } = useAppTranslation();
  const [modal, contextHolder] = Modal.useModal();

  const allRowsKey = data.map((row) => String(row.key));
  const isAllRowsKeySelected = allRowsKey.length === selectedRowKeys.length;

  const [{ loading: loadingPaidCouponsCodes }, getPaidCouponsCodes] = useAsyncFn(
    () =>
      couponApis.couponCheckCodesHadPayment({
        bulkActionType: CouponBulkActionType.CheckCodesHadPayment,
        codes: selectedRowKeys,
      }),
    [selectedRowKeys],
  );

  async function handleBulkAction(finalSelectedRowKeys: string[], bulkActionType: CouponBulkActionType) {
    switch (bulkActionType) {
      case CouponBulkActionType.Archive:
      case CouponBulkActionType.DeArchive:
        await couponApis.couponBulkChangeArchive({ bulkActionType, codes: finalSelectedRowKeys });
        break;
      case CouponBulkActionType.Activate:
      case CouponBulkActionType.Deactivate:
        await couponApis.couponBulkChangeActivation({ bulkActionType, codes: finalSelectedRowKeys });
        break;
      case CouponBulkActionType.Delete:
        await couponApis.couponBulkDelete({ bulkActionType, codes: finalSelectedRowKeys });
        break;
      default:
        break;
    }
    // Clear all selections after operation
    setSelectedRowKeys([]);
    retry();
  }

  async function onBulkActionClick(bulkActionType: CouponBulkActionType): Promise<void> {
    let confirmText = t(`coupon.confirm.${CouponBulkActionType[bulkActionType]}`);
    // selectedRowKeys can be updated so we need to pass the updated values to callBulkAction
    let finalSelectedRowKeys = selectedRowKeys;
    if (bulkActionType === CouponBulkActionType.Delete) {
      const paidCouponsCodes = await getPaidCouponsCodes();
      if (paidCouponsCodes.length) {
        confirmText = t("coupon.confirm.deleteSome", { codes: paidCouponsCodes.join(", ") });
        const notPaidCoupons = selectedRowKeys.filter(
          (selectedCode) => !paidCouponsCodes.find((paidCouponCode) => paidCouponCode === selectedCode),
        );
        finalSelectedRowKeys = notPaidCoupons;
        setSelectedRowKeys(notPaidCoupons);
      }
    }
    // if there is anything to delete
    if (finalSelectedRowKeys.length) {
      modal.confirm({
        cancelText: t("cancel"),
        content: confirmText,
        mask: { closable: true },
        okText: t("ok"),
        onOk: async () => handleBulkAction(finalSelectedRowKeys, bulkActionType),
      });
    } else {
      modal.error({ content: t("coupon.confirm.deleteNone") });
    }
  }

  return (
    <Toast visible={Boolean(selectedRowKeys.length)} loading={loadingPaidCouponsCodes}>
      <Space size={"small"} className={"tw:w-full"} orientation={"vertical"}>
        <div className={"tw:flex tw:justify-start"}>
          <Toast.Item>
            <Space size={"small"}>
              <Checkbox
                onClick={() => {
                  if (isAllRowsKeySelected) {
                    setSelectedRowKeys([]);
                    return;
                  }
                  setSelectedRowKeys(allRowsKey);
                }}
                checked={isAllRowsKeySelected}
                indeterminate={!isAllRowsKeySelected && selectedRowKeys.length > 0}
              >
                <Typography.Text type={"secondary"}>{t("common.operation.selectAll")}</Typography.Text>
              </Checkbox>
              <Typography.Text type={"secondary"}>{`${selectedRowKeys.length} ${t(
                "common.selectedItem",
              )}`}</Typography.Text>
            </Space>
          </Toast.Item>
        </div>
        <Space>
          {isArchived ? (
            <Toast.Item
              label={t("common.operation.unArchive")}
              Icon={() => <UnArchiveOutlined width={16} />}
              onClick={() => onBulkActionClick(CouponBulkActionType.DeArchive)}
            />
          ) : (
            <Toast.Item
              label={t("common.operation.archive")}
              Icon={() => <Archive width={16} />}
              onClick={() => onBulkActionClick(CouponBulkActionType.Archive)}
            />
          )}
          <Toast.Item
            label={t("common.operation.activate")}
            Icon={() => <Bolt width={16} />}
            onClick={() => onBulkActionClick(CouponBulkActionType.Activate)}
          />
          <Toast.Item
            label={t("common.operation.deactivate")}
            Icon={() => <BoltOff width={16} />}
            onClick={() => onBulkActionClick(CouponBulkActionType.Deactivate)}
          />
          <Toast.Item
            label={t("common.operation.delete")}
            Icon={() => <Delete width={16} />}
            onClick={() => onBulkActionClick(CouponBulkActionType.Delete)}
          />
        </Space>
      </Space>
      {contextHolder}
    </Toast>
  );
};
